import { useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Music, Upload } from "lucide-react";

/**
 * The "New track" form on Studio → Upload. Extracted from Dashboard so the
 * page stays under 300 lines and this form can be tested / reused in isolation.
 *
 * Owns two file inputs (audio, cover) and speaks to two upload endpoints.
 * Form state, submit, and the tracks list all live in the parent so the
 * parent can insert the new track into its state on success.
 */
export default function NewTrackForm({
  tForm, setTForm, albums, genreOptions, isSubscribed,
  busy, setBusy, onCreated,
}) {
  const audioRef = useRef();
  const trackCoverRef = useRef();

  const uploadAudio = async () => {
    const file = audioRef.current?.files?.[0];
    if (!file) return toast.error("Pick an audio file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTForm({ ...tForm, audio_path: data.path, duration_sec: data.duration_sec || 0 });
      toast.success("Audio uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const uploadCover = async () => {
    const file = trackCoverRef.current?.files?.[0];
    if (!file) return toast.error("Pick a cover image");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTForm({ ...tForm, cover_path: data.path });
      toast.success("Cover uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!tForm.audio_path) return toast.error("Upload audio first");
    // Cover-art rule: if track is on an album, the album cover always wins —
    // don't bother sending a per-track cover in that case.
    const payload = { ...tForm };
    if (payload.album_id) payload.cover_path = "";
    try {
      const { data } = await api.post("/tracks", payload);
      onCreated(data);
      setTForm({ title: "", audio_path: "", duration_sec: 0, album_id: "", genre: "electronic", is_explicit: false, cover_path: "" });
      if (audioRef.current) audioRef.current.value = "";
      if (trackCoverRef.current) trackCoverRef.current.value = "";
      toast.success("Track published");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <form onSubmit={submit} className="border border-[#2A2A2A] p-6 bg-[#121212]">
      <div className="flex items-center gap-2 mb-4"><Music className="h-5 w-5 text-[#FF4500]" /><h3 className="font-display text-xl">New track</h3></div>

      <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Audio (mp3, wav, m4a)</Label>
      <div className="flex gap-2 mt-2">
        <input data-testid="track-audio-file" ref={audioRef} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/mpeg,audio/mp3,audio/wav,audio/mp4,audio/x-m4a,audio/aac,audio/ogg,audio/*" className="flex-1 font-mono text-xs bg-black border border-[#2A2A2A] p-2" />
        <Button data-testid="track-audio-upload" type="button" onClick={uploadAudio} disabled={busy || !isSubscribed} className="rounded-none bg-white/10 hover:bg-white/20 text-white font-mono text-xs uppercase h-10"><Upload className="h-4 w-4" /></Button>
      </div>
      {tForm.audio_path && <div className="mt-2 font-mono text-[10px] text-[#FF4500] uppercase tracking-widest">✓ Audio ready</div>}

      <Label className="font-mono text-xs uppercase tracking-widest text-gray-400 mt-4 block">Title</Label>
      <Input data-testid="track-title" required value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />

      <Label className="font-mono text-xs uppercase tracking-widest text-gray-400 mt-4 block">Album (optional)</Label>
      <Select value={tForm.album_id || "none"} onValueChange={(v) => setTForm({ ...tForm, album_id: v === "none" ? "" : v })}>
        <SelectTrigger data-testid="track-album-select" className="rounded-none bg-black border-[#2A2A2A] font-mono h-11 mt-2"><SelectValue placeholder="Standalone single" /></SelectTrigger>
        <SelectContent className="bg-black border-[#2A2A2A] font-mono rounded-none">
          <SelectItem value="none">Standalone single</SelectItem>
          {albums.map((a) => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}
        </SelectContent>
      </Select>

      <Label className="font-mono text-xs uppercase tracking-widest text-gray-400 mt-4 block">Genre</Label>
      <Select value={tForm.genre} onValueChange={(v) => setTForm({ ...tForm, genre: v })}>
        <SelectTrigger data-testid="track-genre" className="rounded-none bg-black border-[#2A2A2A] font-mono h-11 mt-2"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-black border-[#2A2A2A] font-mono rounded-none max-h-72">
          {genreOptions.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
        </SelectContent>
      </Select>

      <CoverArtField
        tForm={tForm}
        trackCoverRef={trackCoverRef}
        onUpload={uploadCover}
        busy={busy}
        isSubscribed={isSubscribed}
      />

      <label
        data-testid="track-explicit-toggle"
        className="mt-5 flex items-center justify-between gap-3 border border-[#2A2A2A] px-3 py-2.5 cursor-pointer hover:border-red-600/60 transition-colors"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-4 w-4 bg-red-600 text-white font-mono font-black text-[10px]">E</span>
            <span className="font-mono text-xs uppercase tracking-widest text-gray-200">Explicit content</span>
          </div>
          <div className="font-mono text-[10px] text-gray-500 mt-1">Adds a red "E" badge across the site (parental advisory).</div>
        </div>
        <Switch
          data-testid="track-explicit-switch"
          checked={!!tForm.is_explicit}
          onCheckedChange={(v) => setTForm({ ...tForm, is_explicit: v })}
        />
      </label>

      <Button data-testid="track-submit" type="submit" disabled={!isSubscribed} className="w-full mt-6 rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11">Publish track</Button>
    </form>
  );
}

/** Cover art sub-field — swaps between an upload picker (singles) and an
 *  "inherits from album" note (album tracks). Own component so the parent
 *  form stays flat. */
function CoverArtField({ tForm, trackCoverRef, onUpload, busy, isSubscribed }) {
  const label = <Label className="font-mono text-xs uppercase tracking-widest text-gray-400 mt-4 block">Cover art</Label>;
  if (tForm.album_id) {
    return (
      <>
        {label}
        <div data-testid="track-cover-inherit-note" className="mt-2 border border-[#2A2A2A] bg-black/40 px-3 py-2.5 font-mono text-[11px] text-gray-400 leading-relaxed">
          Using <span className="text-[#FF4500]">album cover</span> — every track on an album shares the record's artwork.
        </div>
      </>
    );
  }
  return (
    <>
      {label}
      <div className="flex gap-2 mt-2">
        <input
          data-testid="track-cover-file"
          ref={trackCoverRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.gif,.heic,image/*"
          className="flex-1 font-mono text-xs bg-black border border-[#2A2A2A] p-2"
        />
        <Button
          data-testid="track-cover-upload"
          type="button"
          onClick={onUpload}
          disabled={busy || !isSubscribed}
          className="rounded-none bg-white/10 hover:bg-white/20 text-white font-mono text-xs uppercase h-10"
        >
          <Upload className="h-4 w-4" />
        </Button>
      </div>
      {tForm.cover_path && (
        <div className="mt-2 flex items-center gap-3">
          <img
            src={fileUrl(tForm.cover_path)}
            className="h-16 w-16 object-cover border border-[#2A2A2A]"
            alt="track cover"
          />
          <div className="font-mono text-[10px] text-[#FF4500] uppercase tracking-widest">✓ Cover ready</div>
        </div>
      )}
      <div className="font-mono text-[10px] text-gray-500 mt-1">Optional. Square image works best. Leave blank for a default artwork.</div>
    </>
  );
}
